import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';

import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { getAllSignInsFromApi, getSignature } from '../../helpers/carrierSignInApi';
import { formatDateTime } from './CarrierSignInUtils';


const CarrierSignInDashboardPage = props => {

    const [allSignIns, setAllSignIns] = useState([]);
    const [displayedSignIns, setDisplayedSignIns] = useState([]);

    const [selectedSignIn, setSelectedSignIn] = useState(null);
    const [selectedSignInSignature, setSelectedSignInSignature] = useState("");

    useEffect(() => {
        if (props.token) {
            getAllSignIns();
        }
    }, [props.token]);

    useEffect(async () => {
        if (selectedSignIn) {
            setSelectedSignInSignature("");
            props.setLoading(true);
            try {
                const signatureResponse = await getSignature(selectedSignIn.sign_in_id, props.token);
                setSelectedSignInSignature(signatureResponse.signature);
            } catch (error) {
                console.log(error);
                props.toast.error("Failed to retrieve signature from IMS database. Please contact a developer");
            }
            props.setLoading(false);
        }
    }, [selectedSignIn]);

    const getAllSignIns = async () => {
        props.setLoading(true);
        try {
            const signInsFromApi = await getAllSignInsFromApi(props.token);
            const mappedSignIns = signInsFromApi.map(signIn => ({
                ...signIn,
                id: signIn.sign_in_id,
                sign_in_time: formatDateTime(signIn.sign_in_time),
                sign_out_time: signIn.sign_out_time ? formatDateTime(signIn.sign_out_time) : ""
            }));
            setAllSignIns(mappedSignIns);
            setDisplayedSignIns(mappedSignIns);
        } catch (error) {
            console.log(error.message);
            props.toast.error("Failed to retrieve sign-ins from IMS database. Please contact a developer");
        }
        props.setLoading(false);
    };

    const filterSignInsByDate = (selectedDate) => {
        if (selectedDate) {
            const shortenedSelectedDate = selectedDate.format("M/D/YYYY");

            const filtered = allSignIns.filter(signIn => {
                const indexOfComma = signIn.sign_in_time.indexOf(",");
                const shortenedSignInDate = signIn.sign_in_time.slice(0, indexOfComma);
                return shortenedSignInDate === shortenedSelectedDate;
            });

            setDisplayedSignIns(filtered);            
        } else {
            setDisplayedSignIns(allSignIns);
        }
    };

    const defaultColumnAttributes = {
        flex: 1,
        editable: false,
        minWidth: 100,
        maxWidth: 200
    };

    const signInTableColumns = [{
        ...defaultColumnAttributes,
        minWidth: 50,
        maxWidth: 100,
        field: 'sign_in_id',
        headerName: "Sign-in ID",
    }, {
        ...defaultColumnAttributes,
        field: 'driver_name',
        headerName: "Driver name"
    }, {
        ...defaultColumnAttributes,
        minWidth: 50,
        maxWidth: 80,
        field: 'scac',
        headerName: "SCAC"
    }, {
        ...defaultColumnAttributes,
        minWidth: 50,
        maxWidth: 120,
        field: 'warehouse',
        headerName: "Warehouse"
    }, {
        ...defaultColumnAttributes,
        field: 'pro_number',
        headerName: "PRO number"
    }, {
        ...defaultColumnAttributes,
        field: 'sign_in_time',
        headerName: "Sign-in time"
    }, {
        ...defaultColumnAttributes,
        field: 'sign_out_time',
        headerName: "Sign-out time"
    }];

    const listItemStyles = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };

    const listItemTextStyles = {
        mt: -1,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        paddingLeft: 2,
        paddingRight: 2
    };

    return (
        <Box display="flex" flexDirection="column" className={props.isMobile ? "mobile-top" : "desktop-top"}>
            <h1>Carrier Sign-In Dashboard</h1>

            <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                    sx={{ width: "200px" }}
                    label="Sign-in date"
                    onChange={filterSignInsByDate}
                />
            </LocalizationProvider>

            <Box className={props.isMobile ? "mobile-table-box" : "desktop-table-box"}>
                <DataGrid
                    style={{ width: "70%", marginLeft: "0%" }}
                    rows={displayedSignIns}
                    className={props.isMobile ? "mobile-table" : "desktop-table"}
                    columns={signInTableColumns}
                    rowsPerPageOption={[25, 50, 100]}
                    checkboxSelection={false}
                    onRowClick={event => { setSelectedSignIn(event.row)}}
                />
            </Box>

            <Drawer open={!!selectedSignIn} onClose={() => setSelectedSignIn(null)} anchor="bottom">
                {selectedSignIn && <Box className={props.isMobile ? "mobile-table-box" : "desktop-table-box"} style={{ height: "500px", width: "80%", marginLeft: "5%" }}>

                    <Card sx={{ height: 350, width: 400, display: "inline-block", margin: 2 }}>
                        <CardContent>
                            <Box sx={{ p: "20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -2.5, mb: -3.8 }}>
                                <Typography gutterBottom variant="h5" component="div">Sign-in Info</Typography>
                            </Box>
                            <Box sx={{ p: "20px", width: "100%" }}>
                                <List dense>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Sign-in ID" secondary={selectedSignIn.sign_in_id} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Driver" secondary={selectedSignIn.driver_name} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="SCAC" secondary={selectedSignIn.scac} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Truck number" secondary={selectedSignIn.truck_number} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Warehouse" secondary={selectedSignIn.warehouse} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="PRO number" secondary={selectedSignIn.pro_number || "N/A"} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Delivered goods" secondary={selectedSignIn.delivered_goods || "N/A"} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Sign-in time" secondary={selectedSignIn.sign_in_time || "N/A"} />
                                    </ListItem>
                                    <ListItem disableGutters sx={listItemStyles}>
                                        <ListItemText sx={listItemTextStyles} primary="Sign-out time" secondary={selectedSignIn.sign_out_time} />
                                    </ListItem>
                                </List>
                            </Box>
                        </CardContent>
                    </Card>
                    <Card sx={{ width: 480, height: 350, display: "inline-block", margin: 2 }}>
                        <CardContent>
                            <Box sx={{ p: "20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -2.5, mb: -3.8 }}>
                                <Typography gutterBottom variant="h5" component="div">Signature</Typography>
                            </Box>
                            <Box sx={{ p: "20px", width: "100%" }}>
                                {props.isLoading === true && <div>Loading signature</div>}
                                {selectedSignInSignature && <img style={{ width: "400px", height: "100px" }} src={selectedSignInSignature} alt="Red dot" />}
                            </Box>
                        </CardContent>
                    </Card>

                </Box>}
            </Drawer>
        </Box>
    );
};

export default CarrierSignInDashboardPage;